import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft, FiArrowRight, FiPlay } from 'react-icons/fi';
import { useAuth } from './AuthContext';

const timeLeft = (item) => {
  if (!item.durationSeconds) return 'Just started';
  const minutes = Math.round(Math.max(0, item.durationSeconds - (item.progressSeconds || 0)) / 60);
  if (minutes < 1) return 'Almost done';
  if (minutes < 60) return `${minutes} min left`;
  return `${Math.floor(minutes / 60)} h ${minutes % 60} min left`;
};

/** Resume rail for signed-in viewers — hidden entirely when there is nothing to resume. */
export default function ContinueWatching() {
  const { user, experience } = useAuth();
  const items = useMemo(
    () => Object.values(experience?.progress || {})
      .filter(item => !item.completed && item.id)
      .sort((a, b) => (b.lastWatchedAt || 0) - (a.lastWatchedAt || 0))
      .slice(0, 16),
    [experience?.progress]
  );

  const rail = useRef(null);
  const [edges, setEdges] = useState({ start: true, end: true });

  useEffect(() => {
    const element = rail.current;
    if (!element) return undefined;
    const update = () => setEdges({
      start: element.scrollLeft < 4,
      end: element.scrollLeft + element.clientWidth >= element.scrollWidth - 4,
    });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    element.addEventListener('scroll', update, { passive: true });
    return () => { observer.disconnect(); element.removeEventListener('scroll', update); };
  }, [items]);

  if (!user || !items.length) return null;

  const scroll = direction => rail.current.scrollBy({
    left: direction * rail.current.clientWidth * .85,
    behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
  });

  return (
    <section className="continue-rail" aria-labelledby="continue-watching">
      <header>
        <div>
          <span className="rule-kicker">Pick up where you left off</span>
          <h2 id="continue-watching">Continue watching</h2>
        </div>
        <div className="continue-rail-actions">
          <Link to="/orbit">All {items.length}</Link>
          <div className="rail-controls">
            <button type="button" aria-label="Previous titles" aria-controls="continue-list" disabled={edges.start} onClick={() => scroll(-1)}><FiArrowLeft /></button>
            <button type="button" aria-label="More titles" aria-controls="continue-list" disabled={edges.end} onClick={() => scroll(1)}><FiArrowRight /></button>
          </div>
        </div>
      </header>
      <div className="continue-list" id="continue-list" ref={rail}>
        {items.map(item => {
          const percentage = item.durationSeconds
            ? Math.min(100, Math.round((item.progressSeconds || 0) / item.durationSeconds * 100))
            : 0;
          const art = item.backdropPath || item.posterPath;
          const episode = item.season ? `S${item.season} E${item.episode}` : null;
          return (
            <Link
              key={`${item.mediaType}_${item.id}`}
              className="continue-card"
              to={`/${item.mediaType || 'movie'}/${item.id}`}
              state={{ watchNow: true }}
              aria-label={`Resume ${item.title}`}
            >
              <div className="continue-still bp">
                {art && <img src={`https://image.tmdb.org/t/p/w780${art}`} alt="" loading="lazy" />}
                <span className="continue-play" aria-hidden="true"><FiPlay /></span>
                <span className="continue-resume" aria-hidden="true">Resume</span>
                <span className="continue-track"><b style={{ width: `${percentage}%` }} /></span>
              </div>
              <div className="continue-copy">
                <strong>{item.title}</strong>
                <small>
                  {episode && <em>{episode}</em>}
                  <span>{timeLeft(item)}</span>
                  {percentage > 0 && <i>{percentage}%</i>}
                </small>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}
